<template>
  <transition appear>
    <owal-banner
      key="owal header banner container"
      :class="`owal header banner container ${showSecondaryView || showOptionalView ? 'open' : 'closed'} ${hasSingleAlertSource ? 'full-width' : ''}`"
    >
      <div class="owal header banner primary" >
        <owal-event-banner-timeline
          class="owal header banner timeline"
          :dot-color="dotColor"
        />
        <div class="owal header banner text">
          <h1>
            {{ title }}
          </h1>

          <h2>
            {{ subtitle }}
          </h2>
        </div> <!-- end text -->
        <div class="owal header banner accessories">
          <owal-banner-button
            v-if="showSecondaryButton"
            v-bind="new OwalButtonConfiguration({
              identifier: 'secondary-button',
              title: secondaryButtonTitle,
              subtitle: secondaryButtonSubtitle,
              open: showSecondaryView,
              callback: open,
              accessoryType: 'static'
            })"
          />
          <owal-banner-button
            v-if="optionalView"
            v-bind="new OwalButtonConfiguration({
              identifier: 'optional-button',
              title: options.optionalButtonTitle,
              subtitle: options.optionalButtonSubtitle,
              open: showOptionalView,
              callback: open,
              accessoryType: 'dynamic'
            })"
          />
        </div>
      </div> <!-- end primary content -->
      <!--
    <div
      class="owal header banner secondary"
      v-if="secondaryView && showSecondaryView">
      <component
        :is="secondaryView"
        @close='showSecondaryView = false'
        :options="options" />
    </div>
    -->
      <div
        v-if="optionalView && showOptionalView"
        class="owal header banner optional"
      >
        <component
          :is="optionalView"
          :options="options"
          @close="showOptionalView = false"
        />
      </div>
    </owal-banner>
  </transition>
</template>

<script>
import { OwalButtonConfiguration } from '../'
import OwalBanner from './OwalBanner'
import OwalEventBannerTimeline from './OwalEventBannerTimeline'
import { OwalBannerButton } from '../buttons/'
import { mapGetters } from 'vuex'

export default {
  name: 'OwalBannerSmall',
  components: {
    OwalBanner,
    OwalEventBannerTimeline,
    OwalBannerButton,
  },
  /**
   * @prop {String} title
   * @prop {String} subitle
   * @prop {String} secondaryButtonTitle The title of the secondary button.
   * @prop {String} secondaryButtonSubtitle The subtitle of the secondary button.
   * @prop {Object} options dictionary of options for the component
   */
  props: {
    title: {
      type: String,
    },
    subtitle: {
      type: String,
    },
    secondaryButtonTitle: {
      type: String,
      default: 'More',
    },
    secondaryButtonSubtitle: {
      type: String,
      default: undefined,
    },
    secondaryView: {
      type: Object,
      default: undefined,
    },
    optionalView: {
      default: undefined,
    },
    options: {
      type: Object,
      default: function () {
        return {}
      },
    },
    /**
     * @prop {Boolean} showSecondaryButton Controls hiding the secondary button.
     *
     * The default value is true for backwards compatibility.
     */
    showSecondaryButton: {
      default: true,
      type: Boolean,
    },
    /**
     * @prop {String} dotColor A prop which controls the color of the dot in the timeline.
     */
    dotColor: {
      default: undefined,
      type: String,
    },
  },
  data () {
    return {
      showSecondaryView: false,
      showOptionalView: false,
      OwalButtonConfiguration,
    }
  },
  // TODO: make secondary-view component dynamic/functional
  computed: {
    ...mapGetters({
      hasSingleAlertSource: 'hasSingleAlertSource',
    }),
    dynamicSecondaryView () {
      if (this.showSecondaryView) {
        return this.secondaryView
      } else if (this.showOptionalView) {
        return this.showOptionalView
      }
      return undefined
    },
  },
  methods: {
    open (payload) {
      const target = payload.identifier
      if (target === 'secondary-button') {
        this.showSecondaryView = !this.showSecondaryView
        this.showOptionalView = false
        this.$emit('secondaryButtonClick')
      } else if (target === 'optional-button') {
        this.showOptionalView = !this.showOptionalView
        this.showSecondaryView = false
      }
    },
  },
}
</script>

<style scoped>
.owal.header.banner.container {
  display: flex;
  flex-direction: column;
  width: 42.8571428571rem;
  margin-left: 1.5rem;
  /* height: 100%; */
}

owal.header.banner.container.full-width {
  margin-left: 1.5rem;
}

.owal.header.banner.container.open {
  height: auto;
}

/* Previous fixed height */
.owal.header.banner.container.closed,
.owal.header.banner.primary {
  min-height: 4.2857142857rem;
}

/*
  Parent position is set to relative and child to absolute in order to make line full height: https://stackoverflow.com/a/15389545.
  Because the width is hard coded, we can get away with setting the margin-left on the banner text to counter the lost offset from absolute positoning.
*/
.owal.header.banner.primary {
  flex-shrink: 0;
  width: 100%;
  display: flex;
  align-items: center;
  flex-direction: row;
  position: relative;
}

.owal.header.banner.timeline {
  width: 6.74rem;
  flex-direction: row-reverse;
  position: absolute;
}

.owal.header.banner.text {
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  flex-grow: 2;
  margin-left: 7.5rem;
}

/* Title: */
.owal.banner.header.text h1 {
  font-family: 'Lato-Regular', sans-serif;
  font-size: 1.7142857143rem;
  color: #475669;
  letter-spacing: 0;
  margin: 0;
}

/* Subtitle: */
.owal.banner.header.text h2 {
  font-family: 'Lato-Italic', sans-serif;
  font-size: 0.8571428571rem;
  color: #8492a6;
  letter-spacing: 0;
  margin: 0;
  white-space: nowrap;
  text-overflow: ellipsis;
  max-width: 28rem;
  overflow: hidden;
}

.owal.banner.header.accessories {
  display: flex;
  flex-direction: column;
  align-content: center;
  justify-content: center;
  padding: 0 0.5rem;
  width: auto;
}

.owal.header.banner.secondary {
  overflow: hidden;
  width: 100%;
  border-bottom-left-radius: 4px;
  border-bottom-right-radius: 4px;
}

.owal.header.banner.optional {
  overflow: hidden;
  width: 100%;
  border-bottom-left-radius: 4px;
  border-bottom-right-radius: 4px;
}

/* --- animation --- */
.v-enter-active,
.v-leave-active {
  transition: opacity 300ms ease, height 300ms ease;
}

.v-enter,
.v-leave-to {
  opacity: 0;
  height: 0;
}

.v-enter-to,
.v-leave {
  opacity: 1;
  height: auto;
}
</style>
